// JOBE-0167.2: MemoryListRow — three-column row in the Memories list.
//
// Columns: subject URN + body snippet · lifecycle pill · confidence band + owner.
// URN click-to-copy per D15 (no <title>, no [role=tooltip]).
// Lifecycle tone map → StatusPill (JOBE-0158.3 followup, the 4 lifecycle tones).

import { useState } from 'react';
import { StatusPill } from '../../components/StatusPill/StatusPill.jsx';

// lifecycle (wire) → StatusPill tone
const LIFECYCLE_TONE = {
  'active':              'active',
  'superseded':          'superseded',
  'tombstoned':          'tombstoned',
  'candidate-implicit':  'candidate',
};

// Confidence band thresholds: low < 0.5, medium < 0.8, high >= 0.8.
function confidenceBand(score) {
  if (score == null) return 'unknown';
  if (score >= 0.8) return 'high';
  if (score >= 0.5) return 'medium';
  return 'low';
}

/**
 * @param {object} props
 * @param {object} props.memory
 * @param {(urn: string) => void} props.onExpand
 */
export function MemoryListRow({ memory, onExpand }) {
  const tone = LIFECYCLE_TONE[memory.lifecycle] || 'candidate';
  const band = confidenceBand(memory.confidence);

  return (
    <button
      type="button"
      className="memory-list-row"
      data-testid="memory-list-row"
      data-urn={memory.urn}
      onClick={() => onExpand(memory.urn)}
    >
      <div className="memory-list-row__col memory-list-row__col--subject">
        <CopyableUrn urn={memory.subject} className="memory-list-row__subject-urn" />
        <div className="memory-list-row__snippet" data-testid="memory-row-snippet">
          {memory.body || ''}
        </div>
      </div>
      <div className="memory-list-row__col memory-list-row__col--lifecycle">
        <StatusPill tone={tone} label={memory.lifecycle} />
      </div>
      <div className="memory-list-row__col memory-list-row__col--confidence">
        <ConfidenceBar score={memory.confidence} band={band} />
        <CopyableUrn urn={memory.owner} className="memory-list-row__owner-urn" />
      </div>
    </button>
  );
}

/**
 * CSS-only 4-segment confidence bar, coloured by band.
 */
function ConfidenceBar({ score, band }) {
  // Number of filled segments: low=1, medium=2, high=4, unknown=0.
  let filled = 0;
  if (band === 'low') filled = 1;
  else if (band === 'medium') filled = 2;
  else if (band === 'high') filled = 4;
  return (
    <div
      className={`memory-confidence-bar memory-confidence-bar--${band}`}
      data-testid="memory-confidence-bar"
      data-band={band}
      data-score={score == null ? '' : String(score)}
      aria-label={`confidence ${band}${score == null ? '' : ` (${score.toFixed(2)})`}`}
    >
      {[0, 1, 2, 3].map((i) => (
        <span
          key={i}
          className={`memory-confidence-bar__seg${i < filled ? ' memory-confidence-bar__seg--on' : ''}`}
        />
      ))}
    </div>
  );
}

/**
 * Click-to-copy span. D15: no <title>, no [role=tooltip]. Hover reveals a small
 * clipboard glyph (handled by CSS via :hover on the parent row). On click,
 * navigator.clipboard.writeText(urn) and flash "Copied" for ~2s (the glyph
 * becomes a checkmark; same pattern as JOBE-0148.1 CapabilitiesTab).
 */
function CopyableUrn({ urn, className }) {
  const [copied, setCopied] = useState(false);

  function handleClick(e) {
    // Don't trigger row expand when copying.
    e.stopPropagation();
    if (!urn) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(urn).catch(() => {});
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleKey(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      handleClick(e);
    }
  }

  return (
    <span
      className={`memory-copyable-urn${copied ? ' memory-copyable-urn--copied' : ''}${className ? ` ${className}` : ''}`}
      role="button"
      tabIndex={0}
      data-testid="memory-copyable-urn"
      data-urn={urn || ''}
      onClick={handleClick}
      onKeyDown={handleKey}
    >
      <span className="memory-copyable-urn__text">{urn || ''}</span>
      <span
        className="memory-copyable-urn__glyph"
        aria-hidden="true"
        data-testid="memory-copyable-urn-glyph"
      >
        {copied ? '✓ Copied' : '⎘'}
      </span>
    </span>
  );
}

export default MemoryListRow;
